import { useMemo } from 'react';
import { Card, Title, Text, Stack, Badge } from '@mantine/core';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import type { AggregatedSlot } from '../types/prediction-types';

interface PredictionChartProps {
  slots: AggregatedSlot[];
  date: string;
}

export default function PredictionChart({ slots, date }: PredictionChartProps) {
  const chartData = useMemo(() => {
    return slots
      .filter(slot => slot.slot_date === date)
      .sort((a, b) => a.slot_start_time.localeCompare(b.slot_start_time))
      .map(slot => {
        const startTime = dayjs(slot.slot_start_time).format('h:mm A');
        const endTime = dayjs(slot.slot_end_time).format('h:mm A');
        const timeRange = `${startTime}-${endTime}`;
        const gameCount = slot.slot_category.split('+').length;
        const prepRatio = slot.duration_minutes > 0 
          ? parseFloat((slot.predicted_preparation_time / slot.duration_minutes).toFixed(2))
          : 0;
        
        return {
          time: timeRange,
          startTime,
          endTime,
          revenue: parseFloat(slot.predicted_revenue.toFixed(2)),
          prepRatio,
          prepTime: parseFloat(slot.predicted_preparation_time.toFixed(1)),
          orders: parseFloat(slot.predicted_orders.toFixed(1)),
          employees: slot.employees_needed,
          gameCount,
          duration: slot.duration_minutes,
        };
      });
  }, [slots, date]);

  if (chartData.length === 0) {
    return null;
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;

    return (
      <Card shadow="md" padding="sm" withBorder>
        <Stack gap="xs">
          <Text size="sm" fw={600}>{data.time}</Text>
          <Text size="xs" c="dimmed">{data.duration} min · {data.gameCount} {data.gameCount === 1 ? 'game' : 'games'}</Text>
          <Badge size="lg" color="orange">
            Ratio: {data.prepRatio}× ({data.prepTime} min prep)
          </Badge>
          <Badge 
            size="sm" 
            color={data.employees >= 3 ? 'red' : data.employees >= 2 ? 'yellow' : 'cyan'}
          >
            Actual staff: {data.employees} {data.employees === 1 ? 'employee' : 'employees'}
          </Badge>
          <Badge size="sm" color="blue">{data.orders} orders · ${data.revenue}</Badge>
        </Stack>
      </Card>
    );
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <div>
          <Title order={4}>Staff Requirements Timeline</Title>
          <Text size="sm" c="dimmed">
            {dayjs(date).format('dddd, MMMM D, YYYY')} · Prep time to slot duration ratio
          </Text>
        </div>

        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
            
            <XAxis 
              dataKey="time" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            
            <YAxis 
              label={{ value: 'Staff Need Ratio', angle: -90, position: 'insideLeft', style: { fontSize: 14 } }}
              tick={{ fontSize: 12 }}
            />
            
            <Tooltip content={<CustomTooltip />} />
            
            <Legend 
              wrapperStyle={{ paddingTop: '10px' }}
              iconType="rect"
            />
            
            {/* Prep ratio bars */}
            <Bar 
              dataKey="prepRatio" 
              fill="#fab005"
              name="Staff Need Ratio"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>

        <Stack gap="xs">
          <Text size="sm" fw={600}>Understanding the ratio:</Text>
          <Text size="xs" c="dimmed">
            Ratio = Prep Time ÷ Slot Duration. A ratio of 2.0 means you need 2 employees 
            (prep time is twice the slot duration). Values are shown before rounding up to actual staff count.
          </Text>
        </Stack>
      </Stack>
    </Card>
  );
}
